import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { ApiError } from '@/lib/api';
import {
  customerNameDisplay,
  formatOnlineDuration,
  getOnlineEmployeesApi,
  getTodayCallsApi,
  type OnlineEmployeesView,
  type TodayCallsView,
} from '@/lib/overview-drilldown';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/empty-state';
import { QcResultBadge } from '@/components/ui/status-badge';
import { formatDateTime, formatScore } from '@/lib/format';

/**
 * Issue #260 — 数据总览「在线员工 / 今日通话」KPI 下钻抽屉。
 *
 * 数据全部来自真实后端分页接口（GET /admin/overview/online-employees、/today-calls），
 * orgId 隔离 + RBAC 由后端强制；非 ok / 非 JSON 由 apiClient 统一抛 ApiError，这里渲染错误态。
 * 客户名沿用 #178 隐私规则（customerNameDisplay 优先完整名→脱敏名→匿名）。绝不前端伪造行。
 */
export type DrilldownKind = 'online' | 'today' | null;

const PAGE_SIZE = 20;

export function OverviewDrilldownDrawer({
  kind,
  onClose,
}: {
  kind: DrilldownKind;
  onClose: () => void;
}) {
  if (kind === null) return null;
  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-slate-900/40"
      role="dialog"
      aria-modal="true"
      data-testid="overview-drilldown-drawer"
      onClick={onClose}
    >
      <div
        className="flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-900">
            {kind === 'online' ? '在线员工明细' : '今日通话明细'}
          </h2>
          <button
            type="button"
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
            onClick={onClose}
            aria-label="关闭明细"
            data-testid="overview-drilldown-close"
          >
            <X className="h-5 w-5" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {kind === 'online' ? <OnlineEmployeesPanel /> : <TodayCallsPanel />}
        </div>
      </div>
    </div>
  );
}

function Pager({
  page,
  total,
  onPage,
}: {
  page: number;
  total: number;
  onPage: (p: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  return (
    <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
      <span>
        共 {total} 条 · 第 {page}/{totalPages} 页
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          className="rounded-md border border-slate-300 px-2 py-1 disabled:opacity-40"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          data-testid="overview-drilldown-prev"
        >
          上一页
        </button>
        <button
          type="button"
          className="rounded-md border border-slate-300 px-2 py-1 disabled:opacity-40"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
          data-testid="overview-drilldown-next"
        >
          下一页
        </button>
      </div>
    </div>
  );
}

function OnlineEmployeesPanel() {
  const [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: ['admin', 'overview', 'online-employees', page],
    queryFn: () => getOnlineEmployeesApi({ page, limit: PAGE_SIZE }),
  });
  if (query.isLoading) return <LoadingState label="正在加载在线员工…" />;
  if (query.isError || !query.data) {
    return (
      <ErrorState
        title="加载在线员工失败"
        description={query.error instanceof ApiError ? query.error.message : '请稍后重试。'}
        retry={() => void query.refetch()}
      />
    );
  }
  const view: OnlineEmployeesView = query.data;
  if (view.items.length === 0) {
    return (
      <EmptyState
        title="当前无在线员工"
        description={`近 ${view.onlineWindowMinutes} 分钟内没有活跃账号。`}
      />
    );
  }
  return (
    <div>
      <p className="mb-3 text-xs text-slate-500">
        在线判定：近 {view.onlineWindowMinutes} 分钟内有登录活动；当天在线时长由登录/登出记录推导（近似值）。
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm" data-testid="online-employees-table">
          <thead className="text-xs text-slate-500">
            <tr>
              <th className="px-2 py-2 text-left font-medium">姓名</th>
              <th className="px-2 py-2 text-left font-medium">部门 / 岗位</th>
              <th className="px-2 py-2 text-left font-medium">最近登录</th>
              <th className="px-2 py-2 text-right font-medium">当天在线时长</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-slate-700">
            {view.items.map((e) => (
              <tr key={e.employeeId} className="hover:bg-slate-50">
                <td className="px-2 py-2 font-medium text-slate-800">{e.name}</td>
                <td className="px-2 py-2 text-slate-500">
                  {e.deptName ?? '未分配部门'}
                  {e.jobTitle ? ` · ${e.jobTitle}` : ''}
                </td>
                <td className="px-2 py-2 text-slate-500">
                  {e.lastLoginAt ? formatDateTime(e.lastLoginAt) : '—'}
                </td>
                <td className="px-2 py-2 text-right font-semibold text-accent-600">
                  {formatOnlineDuration(e.onlineDurationSec)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager page={view.page} total={view.total} onPage={setPage} />
    </div>
  );
}

function TodayCallsPanel() {
  const [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: ['admin', 'overview', 'today-calls', page],
    queryFn: () => getTodayCallsApi({ page, limit: PAGE_SIZE }),
  });
  if (query.isLoading) return <LoadingState label="正在加载今日通话…" />;
  if (query.isError || !query.data) {
    return (
      <ErrorState
        title="加载今日通话失败"
        description={query.error instanceof ApiError ? query.error.message : '请稍后重试。'}
        retry={() => void query.refetch()}
      />
    );
  }
  const view: TodayCallsView = query.data;
  if (view.items.length === 0) {
    return (
      <EmptyState
        title="今日暂无通话"
        description="员工 APP 开始上传录音后，这里会出现今日通话明细。"
      />
    );
  }
  return (
    <div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm" data-testid="today-calls-table">
          <thead className="text-xs text-slate-500">
            <tr>
              <th className="px-2 py-2 text-left font-medium">员工</th>
              <th className="px-2 py-2 text-left font-medium">客户</th>
              <th className="px-2 py-2 text-left font-medium">开始</th>
              <th className="px-2 py-2 text-left font-medium">时长</th>
              <th className="px-2 py-2 text-left font-medium">AI 评分</th>
              <th className="px-2 py-2 text-right font-medium">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-slate-700">
            {view.items.map((c) => (
              <tr key={c.callRecordId} className="hover:bg-slate-50">
                <td className="px-2 py-2 font-medium text-slate-800">{c.employeeName ?? '—'}</td>
                <td className="px-2 py-2 text-slate-600">{customerNameDisplay(c)}</td>
                <td className="px-2 py-2 text-slate-500">{formatDateTime(c.startedAt)}</td>
                <td className="px-2 py-2 text-slate-500">{formatCallDuration(c.durationSec)}</td>
                <td className="px-2 py-2">
                  <span className="font-semibold text-accent-600">{formatScore(c.aiScore ?? c.qcScore)}</span>
                  {c.qcResult ? (
                    <span className="ml-2 inline-block align-middle">
                      <QcResultBadge result={c.qcResult} />
                    </span>
                  ) : null}
                </td>
                <td className="px-2 py-2 text-right">
                  <Link
                    to={`/calls/${c.callRecordId}`}
                    className="text-xs font-medium text-accent-600 hover:underline"
                  >
                    查看报告
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager page={view.page} total={view.total} onPage={setPage} />
    </div>
  );
}

function formatCallDuration(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${`${s}`.padStart(2, '0')}`;
}
